import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IAdminDataset, IAdminUser, bulkAddDatasetPermissions, useAdminUsersQuery } from '../../api/admin';

interface Props {
	open: boolean;
	onClose: () => void;
	datasets: IAdminDataset[];
}

export default function GrantDatasetPermissionDialog({ open, onClose, datasets }: Props) {
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery();

	const [step, setStep] = useState<'select' | 'confirm'>('select');
	const [selectedUsers, setSelectedUsers] = useState<IAdminUser[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [succeeded, setSucceeded] = useState(false);
	const [grantedCount, setGrantedCount] = useState(0);

	const eligibleUsers = (allUsers ?? []).filter((u) => u.is_active);
	const totalGrants = datasets.length * selectedUsers.length;

	function resetAndClose() {
		setStep('select');
		setSelectedUsers([]);
		setError(null);
		setSucceeded(false);
		onClose();
	}

	async function handleConfirm() {
		setSubmitting(true);
		setError(null);
		try {
			const granted = await bulkAddDatasetPermissions(
				datasets.map((ds) => ds.id),
				selectedUsers.map((u) => u.id),
				new Date().toISOString().split('T')[0],
			);
			setGrantedCount(granted.length);
			setSucceeded(true);
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{succeeded ? (
				<>
					<DialogTitle>Permissions Granted</DialogTitle>
					<DialogContent>
						{grantedCount < totalGrants ? (
							<Alert severity="warning">
								Granted {grantedCount} of {totalGrants} permissions. The skipped ones and the reason are in the
								admin notification email.
							</Alert>
						) : (
							<Alert severity="success">
								{totalGrants} permission{totalGrants !== 1 ? 's' : ''} granted successfully.
							</Alert>
						)}
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>Done</Button>
					</DialogActions>
				</>
			) : step === 'select' ? (
				<>
					<DialogTitle>Grant Dataset Access</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Granting access to {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}. Select
							which users should receive access.
						</Typography>
						<Autocomplete
							multiple
							options={eligibleUsers}
							loading={usersLoading}
							disableCloseOnSelect
							filterSelectedOptions
							value={selectedUsers}
							onChange={(_, value) => setSelectedUsers(value)}
							getOptionLabel={(u) =>
								u.full_name ? `${u.full_name} (${u.email})` : u.email
							}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderTags={(value, getTagProps) =>
								value.map((u, index) => (
									<Chip
										{...getTagProps({ index })}
										label={u.full_name ?? u.email}
										size="small"
									/>
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Target Users"
									placeholder="Search by name or email…"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{usersLoading && <CircularProgress color="inherit" size={16} />}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose}>Cancel</Button>
						<Button
							variant="contained"
							onClick={() => setStep('confirm')}
							disabled={selectedUsers.length === 0}
						>
							Next ({selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''})
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Confirm Permission Grant</DialogTitle>
					<DialogContent>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography variant="body2" gutterBottom>
							Granting <strong>{totalGrants} permission{totalGrants !== 1 ? 's' : ''}</strong> (
							{datasets.length} dataset{datasets.length !== 1 ? 's' : ''} ×{' '}
							{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}):
						</Typography>

						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							Datasets
						</Typography>
						<List dense disablePadding sx={{ mb: 1 }}>
							{datasets.map((ds) => (
								<ListItem key={ds.id} disableGutters sx={{ py: 0 }}>
									<ListItemText
										primary={ds.name}
										secondary={ds.vendor_name}
										primaryTypographyProps={{ variant: 'body2' }}
										secondaryTypographyProps={{ variant: 'caption' }}
									/>
								</ListItem>
							))}
						</List>

						<Divider sx={{ my: 1 }} />

						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							Recipients
						</Typography>
						<List dense disablePadding sx={{ mb: 1 }}>
							{selectedUsers.map((u) => (
								<ListItem key={u.id} disableGutters sx={{ py: 0 }}>
									<ListItemText
										primary={u.full_name ?? u.email}
										secondary={u.full_name ? u.email : undefined}
										primaryTypographyProps={{ variant: 'body2' }}
										secondaryTypographyProps={{ variant: 'caption' }}
									/>
								</ListItem>
							))}
						</List>

						<Divider sx={{ my: 1 }} />

						<Typography variant="body2" color="text.secondary">
							Access from: <strong>{new Date().toLocaleDateString('en-CA')}</strong> (today)
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => { setError(null); setStep('select'); }} disabled={submitting}>
							Back
						</Button>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleConfirm}
							disabled={submitting}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Granting…' : `Grant ${totalGrants}`}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
